import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";
import { useConfirm } from "@/components/ConfirmDialog";

// ============================================================================
// USER STATS — Faza 1 dashboard (6 widgets)
// Backend: GET /api/admin/user-stats (60s in-memory cache).
// ============================================================================
const STAT_PIE_COLORS = ["#0F172A", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981",
                          "#3B82F6", "#EF4444", "#14B8A6"];

const LANG_LABELS = { pl: "🇵🇱 Polski", en: "🇬🇧 English", hr: "🇭🇷 Hrvatski" };


function UserStatsTab() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const [compareTo, setCompareTo] = useState(null); // snapshot object
  const [snapLabel, setSnapLabel] = useState("");
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [digestPreview, setDigestPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: stats }, { data: snaps }, { data: sched }] = await Promise.all([
        teacherApi.get("/admin/user-stats"),
        teacherApi.get("/admin/stats/snapshots"),
        teacherApi.get("/admin/scheduler/status").catch(() => ({ data: null })),
      ]);
      setData(stats);
      setSnapshots(snaps.snapshots || []);
      setSchedulerStatus(sched);
    } catch { toast.error("Nie udało się pobrać statystyk"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createSnapshot = async () => {
    if (snapLabel.trim().length < 2) {
      toast.error("Podaj etykietę (min. 2 znaki)"); return;
    }
    try {
      await teacherApi.post("/admin/stats/snapshot", { label: snapLabel.trim() });
      toast.success(`Snapshot „${snapLabel}" zapisany`);
      setSnapLabel("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  };

  const deleteSnapshot = async (id) => {
    const ok = await confirm({
      title: "Usunąć snapshot?",
      description: "Ten punkt porównawczy zostanie trwale usunięty. Historia porównań tego snapshotu przestanie być dostępna.",
      confirmText: "Usuń snapshot",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await teacherApi.delete(`/admin/stats/snapshots/${id}`);
      if (compareTo?.id === id) setCompareTo(null);
      load();
    } catch { toast.error("Błąd usuwania"); }
  };

  // Delta helper — returns "+N" / "-N" / "=" for compare view
  const delta = (currentKey, path) => {
    if (!compareTo) return null;
    const cur = path.split(".").reduce((o, k) => o?.[k], data);
    const prev = path.split(".").reduce((o, k) => o?.[k], compareTo);
    if (typeof cur !== "number" || typeof prev !== "number") return null;
    const d = cur - prev;
    if (d === 0) return "=";
    return d > 0 ? `+${d}` : `${d}`;
  };

  if (loading || !data) {
    return (
      <Section icon={TrendingUp} title="Statystyki użytkowników">
        <div className="text-sm text-[#64748B]">Ładowanie...</div>
      </Section>
    );
  }

  const funnelChartData = [
    { step: "Rejestracja", n: data.funnel.registered },
    { step: "Zweryfikowany email", n: data.funnel.verified },
    { step: "Ukończył onboarding", n: data.funnel.onboarded },
    { step: "Ma ≥1 klasę", n: data.funnel.first_class },
    { step: "Ma ≥1 sesję", n: data.funnel.first_session },
  ];
  const langData = data.languages.map((l) => ({
    ...l, name: LANG_LABELS[l.lang] || l.lang,
  }));

  return (
    <Section icon={TrendingUp} title="Statystyki użytkowników">
      {digestPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setDigestPreview(null)}
          data-testid="digest-preview-modal"
        >
          <div
            className="brut border-4 border-[#0F172A] bg-white rounded-md max-w-lg w-full max-h-[85vh] overflow-auto shadow-[8px_8px_0_#0F172A]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b-2 border-[#0F172A] bg-[#FEF08A] flex justify-between items-center">
              <h3 className="font-black text-lg uppercase">📊 Podgląd digestu</h3>
              <button
                onClick={() => setDigestPreview(null)}
                className="brut brut-hover bg-white border-2 border-[#0F172A] rounded px-2 py-0.5 font-bold text-xs"
                data-testid="digest-preview-close"
              >
                Zamknij
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div>
                <b>Okres:</b> {digestPreview.period.from.slice(0, 10)} → {digestPreview.period.to.slice(0, 10)}
                <div className="text-xs text-[#64748B]">
                  Poprzedni tydzień: {digestPreview.period.prev_from?.slice(0, 10)} → {digestPreview.period.prev_to?.slice(0, 10)}
                </div>
              </div>
              <div className="brut border-2 border-[#0F172A] rounded p-3 bg-[#F8FAFC]">
                <div className="font-bold mb-1">👥 Nauczyciele</div>
                <div>Nowi: <b>{digestPreview.teachers.new_total}</b> (poprz. {digestPreview.teachers.new_total_prev}) · zweryfikowani: <b>{digestPreview.teachers.new_verified}</b></div>
                <div className="text-xs mt-1">
                  🎟️ Zaproszeni: <b>{digestPreview.teachers.new_by_tier.invited || 0}</b> ·
                  💳 Płatni: <b>{digestPreview.teachers.new_by_tier.paid || 0}</b> ·
                  🌱 Organiczni: <b>{digestPreview.teachers.new_by_tier.organic || 0}</b>
                </div>
                <div className="text-xs mt-1">Aktywni 7d: <b>{digestPreview.teachers.active_7d}</b> z {digestPreview.teachers.total}</div>
              </div>
              <div className="brut border-2 border-[#0F172A] rounded p-3 bg-[#F8FAFC]">
                <div className="font-bold mb-1">📚 Aktywność</div>
                <div>Klasy: <b>{digestPreview.usage.new_classes}</b> (poprz. {digestPreview.usage.new_classes_prev})</div>
                <div>Sesje: <b>{digestPreview.usage.new_sessions}</b> (poprz. {digestPreview.usage.new_sessions_prev}) · śr. {digestPreview.usage.avg_students_per_session} uczn./sesję</div>
                <div>Submitacje: <b>{digestPreview.usage.new_submissions}</b> (poprz. {digestPreview.usage.new_submissions_prev})</div>
              </div>
              <div className="brut border-2 border-[#0F172A] rounded p-3 bg-[#F8FAFC]">
                <div className="font-bold mb-1">🗺️ Top województwa</div>
                <ul className="text-xs list-disc list-inside">
                  {digestPreview.top_voivodeships.length > 0
                    ? digestPreview.top_voivodeships.map((v, i) => (
                        <li key={i}>{v.voivodeship || <i>brak</i>}: <b>{v.count}</b></li>
                      ))
                    : <li><i>brak nowych nauczycieli w tym tygodniu</i></li>}
                </ul>
              </div>
              <div className="brut border-2 border-[#0F172A] rounded p-3 bg-[#F8FAFC]">
                <div className="font-bold mb-1">💬 Feedback</div>
                <div>Nowe: <b>{digestPreview.feedback.new_count}</b> · Oczekujące: <b>{digestPreview.feedback.pending_count}</b></div>
              </div>
              <div className={`brut border-2 border-[#0F172A] rounded p-3 ${
                (digestPreview.alerts.smtp_pending_high || digestPreview.alerts.bounce_rate_high || digestPreview.alerts.backup_stale)
                  ? "bg-[#FEE2E2]" : "bg-[#D1FAE5]"
              }`}>
                <div className="font-bold mb-1">🚨 Alerty</div>
                {digestPreview.alerts.smtp_pending_high && <div>⚠️ SMTP kolejka: {digestPreview.alerts.smtp_pending} pending</div>}
                {digestPreview.alerts.bounce_rate_high && <div>⚠️ Bounce rate: {digestPreview.alerts.bounce_rate_pct}%</div>}
                {digestPreview.alerts.backup_stale && <div>⚠️ Ostatni backup: {digestPreview.alerts.last_backup_age_h}h temu</div>}
                {!digestPreview.alerts.smtp_pending_high && !digestPreview.alerts.bounce_rate_high && !digestPreview.alerts.backup_stale && "✅ Wszystko w normie"}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#64748B]">
          Aktualizowane co 60s (cache). Wygenerowano:{" "}
          <b>{new Date(data.generated_at).toLocaleString("pl")}</b>
        </p>
        <Button
          onClick={load}
          size="sm"
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="user-stats-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-2" strokeWidth={2.5} />Odśwież
        </Button>
      </div>

      {/* Snapshot controls */}
      <div className="brut border-2 border-[#0F172A] rounded-md bg-[#FEF3C7] p-3 mb-4" data-testid="snapshots-widget">
        <div className="flex flex-wrap gap-2 items-center">
          <b className="text-sm">📸 Snapshoty</b>
          <Input
            value={snapLabel} onChange={(e) => setSnapLabel(e.target.value)}
            placeholder="np. Koniec 2024/25"
            className="brut-input border-2 border-[#0F172A] rounded-md h-9 flex-1 min-w-[160px]"
            maxLength={80} data-testid="snapshot-label-input"
          />
          <Button size="sm" onClick={createSnapshot}
                  className="brut brut-hover bg-[#FEF08A] border-2 border-[#0F172A] font-bold rounded-md"
                  data-testid="snapshot-create-btn">
            Zapisz teraz
          </Button>
        </div>
        {schedulerStatus?.running && (() => {
          const daily = (schedulerStatus.jobs || []).find(j => j.id === "daily_stats_snapshot");
          const weekly = (schedulerStatus.jobs || []).find(j => j.id === "weekly_admin_digest");
          if (!daily?.next_run_at && !weekly?.next_run_at) return null;
          return (
            <div className="mt-2 space-y-1 text-xs" data-testid="scheduler-info">
              {daily?.next_run_at && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="font-bold text-[#64748B]">🤖 Auto-snapshot:</span>
                  <span>codziennie 00:05 UTC · następny: <b>{new Date(daily.next_run_at).toLocaleString("pl")}</b></span>
                  <button
                    onClick={async () => {
                      try {
                        await teacherApi.post("/admin/scheduler/run-daily-snapshot");
                        toast.success("Snapshot dzisiejszego dnia zapisany (idempotentnie).");
                        load();
                      } catch (e) { toast.error(e.response?.data?.detail || "Błąd triggera"); }
                    }}
                    className="brut brut-hover bg-white border-2 border-[#0F172A] rounded px-2 py-0.5 font-bold"
                    data-testid="scheduler-run-now-btn"
                  >
                    Uruchom teraz
                  </button>
                </div>
              )}
              {weekly?.next_run_at && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="font-bold text-[#64748B]">📊 Digest tygodnia:</span>
                  <span>niedziela 20:00 Warszawa · następny: <b>{new Date(weekly.next_run_at).toLocaleString("pl")}</b></span>
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await teacherApi.get("/admin/scheduler/weekly-digest-preview");
                        setDigestPreview(data);
                      } catch (e) { toast.error(e.response?.data?.detail || "Błąd podglądu"); }
                    }}
                    className="brut brut-hover bg-white border-2 border-[#0F172A] rounded px-2 py-0.5 font-bold"
                    data-testid="digest-preview-btn"
                  >
                    Podgląd
                  </button>
                  <button
                    onClick={async () => {
                      const ok = await confirm({
                        title: "Wysłać digest tygodnia teraz?",
                        description: "E-mail zostanie natychmiast wysłany do wszystkich admin-ów oraz na adres z ADMIN_DIGEST_EMAIL (jeśli ustawiony).",
                        confirmText: "Wyślij digest",
                        variant: "default",
                      });
                      if (!ok) return;
                      try {
                        await teacherApi.post("/admin/scheduler/run-weekly-digest");
                        toast.success("Digest wysłany. Sprawdź inbox / /admin > Logi dostarczania.");
                      } catch (e) { toast.error(e.response?.data?.detail || "Błąd wysyłki"); }
                    }}
                    className="brut brut-hover bg-[#FEF08A] border-2 border-[#0F172A] rounded px-2 py-0.5 font-bold"
                    data-testid="digest-send-now-btn"
                  >
                    Wyślij teraz
                  </button>
                </div>
              )}
            </div>
          );
        })()}
        {snapshots.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <span className="text-xs font-bold text-[#64748B]">Porównaj z:</span>
            <button
              onClick={() => setCompareTo(null)}
              className={`brut border-2 border-[#0F172A] rounded px-2 py-0.5 text-xs font-bold ${!compareTo ? "bg-[#0F172A] text-white" : "bg-white"}`}
              data-testid="snapshot-compare-none"
            >
              Brak porównania
            </button>
            {snapshots.map((s) => (
              <div key={s.id} className="flex items-center gap-1">
                <button
                  onClick={() => setCompareTo(s)}
                  className={`brut border-2 border-[#0F172A] rounded px-2 py-0.5 text-xs font-bold ${compareTo?.id === s.id ? "bg-[#8B5CF6] text-white" : "bg-white"}`}
                  data-testid={`snapshot-compare-${s.id}`}
                  title={new Date(s.created_at).toLocaleString("pl") + (s.source === "auto" ? " (auto)" : "")}
                >
                  {s.source === "auto" ? "🤖 " : ""}{s.label}
                </button>
                <button onClick={() => deleteSnapshot(s.id)}
                        className="text-[10px] text-[#EF4444] font-bold"
                        title="Usuń snapshot"
                        data-testid={`snapshot-delete-${s.id}`}>✕</button>
              </div>
            ))}
          </div>
        )}
        {compareTo && (
          <div className="mt-2 text-xs">
            <b>Porównanie z „{compareTo.label}&rdquo;</b> ({new Date(compareTo.created_at).toLocaleDateString("pl")}) —
            {" "}Nauczyciele: {compareTo.users.total} → {data.users.total}{" "}
            <span className="font-bold text-[#8B5CF6]">({delta("total", "users.total")})</span> ·
            Klasy: {compareTo.usage.classes} → {data.usage.classes}{" "}
            <span className="font-bold text-[#8B5CF6]">({delta("classes", "usage.classes")})</span> ·
            Sesje: {compareTo.usage.sessions} → {data.usage.sessions}{" "}
            <span className="font-bold text-[#8B5CF6]">({delta("sessions", "usage.sessions")})</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="user-stats-kpis">
        <KpiTile
          label="Wszyscy" value={data.users.total}
          sub={`Nauczyciele: ${data.users.teachers} · Admini: ${data.users.admins}`}
          testId="kpi-total" accent="#FEF08A"
        />
        <KpiTile
          label="Zweryfikowani"
          value={`${data.users.verified} (${Math.round(100*data.users.verified/Math.max(data.users.total,1))}%)`}
          sub="Kliknęli link aktywacyjny w email"
          testId="kpi-verified" accent="#BBF7D0"
        />
        <KpiTile
          label="Po onboardingu"
          value={`${data.users.onboarded} (${Math.round(100*data.users.onboarded/Math.max(data.users.teachers,1))}%)`}
          sub={`z ${data.users.teachers} nauczycieli`}
          testId="kpi-onboarded" accent="#DDD6FE"
        />
        <KpiTile
          label="Aktywni 7d"
          value={data.users.active_7d}
          sub={`30d: ${data.users.active_30d} · Nowi 7d: ${data.users.registered_7d} · 30d: ${data.users.registered_30d}`}
          testId="kpi-active" accent="#BFDBFE"
        />
      </div>

      <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-4 mb-6" data-testid="funnel-widget">
        <h3 className="font-black text-lg mb-3">Lejek onboardingu</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={funnelChartData} layout="vertical" margin={{ left: 130, right: 30, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="step" tick={{ fontSize: 11, fontWeight: 700 }} width={130} />
              <Tooltip />
              <Bar dataKey="n" fill="#8B5CF6" radius={[0, 4, 4, 0]}>
                {funnelChartData.map((_, i) => (
                  <Cell key={i} fill={STAT_PIE_COLORS[i % STAT_PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="usage-widget">
        <KpiTile
          label="Klasy" value={data.usage.classes}
          sub={`śr. ${data.usage.avg_classes_per_teacher} / nauczyciela`}
          testId="kpi-classes" accent="#FEE2E2"
        />
        <KpiTile
          label="Sesje" value={data.usage.sessions}
          sub={`śr. ${data.usage.avg_sessions_per_teacher} / nauczyciela`}
          testId="kpi-sessions" accent="#DBEAFE"
        />
        <KpiTile
          label="Odpowiedzi uczniów" value={data.usage.submissions}
          sub="Sumaryczne submissions"
          testId="kpi-submissions" accent="#D1FAE5"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-4" data-testid="voivodeship-widget">
          <h3 className="font-black text-lg mb-3">Województwa</h3>
          {data.geo.voivodeships.length === 0 ? (
            <p className="text-sm text-[#64748B] italic">Brak danych o województwach.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.geo.voivodeships}
                    dataKey="count"
                    nameKey="name"
                    innerRadius={40}
                    outerRadius={80}
                    label={(e) => `${e.name} (${e.count})`}
                  >
                    {data.geo.voivodeships.map((_, i) => (
                      <Cell key={i} fill={STAT_PIE_COLORS[i % STAT_PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-4" data-testid="cities-widget">
          <h3 className="font-black text-lg mb-3">Top 10 miast</h3>
          {data.geo.cities.length === 0 ? (
            <p className="text-sm text-[#64748B] italic">Brak danych o miastach.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.geo.cities} layout="vertical" margin={{ left: 80, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontWeight: 700 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#EC4899" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-4 mb-6" data-testid="trend-widget">
        <h3 className="font-black text-lg mb-3">Rejestracje — ostatnie 30 dni</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.registrations_trend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(d) => d.slice(5)}
                interval={4}
              />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone" dataKey="count"
                stroke="#0F172A" strokeWidth={2.5}
                dot={{ fill: "#FEF08A", stroke: "#0F172A", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-4" data-testid="languages-widget">
        <h3 className="font-black text-lg mb-3">Języki użytkowników</h3>
        {langData.length === 0 ? (
          <p className="text-sm text-[#64748B] italic">Brak preferencji językowych.</p>
        ) : (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={langData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 700 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-xs text-[#64748B] mt-2 italic">
          Obecnie tylko PL aktywne — widget gotowy na wielojęzyczność.
        </p>
      </div>

      {/* Widget 7: Segments (invitation-tagged registrations) */}
      {data.segments && (
        <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-4 mt-6" data-testid="segments-widget">
          <h3 className="font-black text-lg mb-3">
            Segmenty rejestracji · Organic:{" "}
            <span className="brut border-2 border-[#0F172A] bg-[#E2E8F0] px-2 py-0.5 rounded text-sm">
              {data.segments.organic}
            </span>
          </h3>
          {data.segments.tagged.length === 0 ? (
            <p className="text-sm text-[#64748B] italic">
              Nikt jeszcze nie zarejestrował się przez link z zaproszeniem.
              Utwórz zaproszenia w zakładce <b>Zaproszenia</b>.
            </p>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.segments.tagged}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                  <XAxis dataKey="segment" tick={{ fontSize: 11, fontWeight: 700 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}




export default UserStatsTab;
